import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { App as AntdApp, Avatar, Button, Dropdown, Flex, Grid, Layout, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LanguageSwitcher } from '@/shared/ui/LanguageSwitcher'
import { ThemeToggle } from '@/shared/ui/ThemeToggle'
import { useAuth } from '@/shared/auth/useAuth'
import { TenantSwitcher } from './TenantSwitcher'
import { BranchSwitcher } from '@/features/branch/BranchSwitcher'

interface AppHeaderProps {
  title: string
  collapsed: boolean
  onToggleCollapsed: () => void
  /**
   * Tor ekranda tugma sig'ib qolgan doimiy ustunni yig'ish o'rniga
   * suzuvchi panelni ochadi — ikkalasi butunlay boshqa harakat, shuning
   * uchun alohida callback (berilmasa desktop xatti-harakati ishlaydi).
   */
  onOpenMobileNav?: () => void
}

export function AppHeader({ title, collapsed, onToggleCollapsed, onOpenMobileNav }: AppHeaderProps) {
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const { modal } = AntdApp.useApp()
  const screens = Grid.useBreakpoint()

  /**
   * Tor ekran — `AppLayout` dagi `isMobile` bilan AYNAN bir xil shart
   * (`md === false`), ataylab: header va yon ustun bitta chegarada
   * o'zgarsin, aks holda oraliq enda ikkalasi bir-biriga zid holatda
   * bo'lardi.
   *
   * ⚠️ Bu yerda `onOpenMobileNav` bor-yo'qligiga qarab bo'lmaydi: u
   * navigatsiya haqidagi qaror, shrift va matn haqidagi emas. Ikkalasi
   * bugun bir vaqtda o'zgaradi, lekin bittasi o'zgarganda ikkinchisi
   * JIMGINA ergashib ketardi.
   */
  const compact = screens.md === false

  /**
   * ⚠️ CHIQISH ENDI TASDIQLANADI.
   *
   * Ilgari menyudagi bitta bosish sessiyani darhol tugatardi. "Chiqish"
   * bandi foydalanuvchi nomining ostida, ya'ni tenant almashtirish yoki
   * profil qidirayotgan odam unga tasodifan tegib ketardi — va qaytish
   * yo'li arzon emas: qayta login, keyin BIR MARTALIK KOD (TOTP
   * majburiy). Telefonni olib, kod kutish — bir necha daqiqa.
   *
   * Shuning uchun tasdiqlash matni oqibatni AYNAN aytadi: qayta kirish
   * kod bilan bo'ladi.
   */
  const handleSignOut = () => {
    modal.confirm({
      title: t('auth.logoutConfirmTitle'),
      content: t('auth.logoutConfirmText'),
      okText: t('auth.logoutConfirmOk'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () => signOut().then(() => navigate('/login', { replace: true })),
    })
  }

  return (
    <Layout.Header
      className="xsib-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        // Tor ekranda oraliq va yon bo'shliq kichrayadi — bu 16px ni
        // sarlavhaga qaytaradi (`.xsib-content` da ham xuddi shu qaror,
        // `global.css` dagi `@media (max-width: 576px)`).
        gap: compact ? 8 : 12,
        padding: compact ? '0 12px' : '0 16px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Button
        type="text"
        aria-label={onOpenMobileNav ? t('nav.openMenu') : collapsed ? t('nav.expand') : t('nav.collapse')}
        icon={onOpenMobileNav ? <MenuUnfoldOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={onOpenMobileNav ?? onToggleCollapsed}
      />

      {/*
        ⚠️ SARLAVHA — TO'RTTA XOSSA, HAR BIRI O'LCHANGAN SABAB BILAN.

        390px da (2026-08-14 tashqi tester, A7) sarlavha 33px enga
        siqilib, HARF-HARF o'ralardi: "Unit types" 75px, "Employees"
        va "Subscriber list" 101px balandlikda — ya'ni 64px li
        header'dan CHIQIB ketardi va sahifaning kimligi o'qilmasdi.

        Sabab ikki qismdan iborat edi:
          · antd `.ant-typography` da `word-break: break-word` turadi,
            ya'ni matnning min-content eni BITTA HARF ga tushadi —
            flex `min-width: auto` himoyasi yo'qoladi;
          · o'ng guruh 285px egallardi va u qisqarmasdi.

        `flex: 1 1 auto` + `minWidth: 0` — sarlavha qolgan bo'sh joyni
        oladi; `nowrap` + `ellipsis` — sig'masa uch nuqta bilan
        kesiladi, HECH QACHON ikkinchi qatorga o'tmaydi. Kesilish
        o'ralishdan afzal: bir qatorlik "Subscriber li…" o'qiladi,
        vertikal harflar ustuni esa yo'q.
      */}
      <Typography.Text
        strong
        // Kesilgan sarlavha to'liq holda shu yerda qoladi.
        title={title}
        style={{
          fontSize: 16,
          flex: '1 1 auto',
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </Typography.Text>

      {/* `marginLeft: auto` OLIB TASHLANDI: sarlavha endi o'zi cho'ziladi.
          `flexShrink: 0` — boshqaruvlar bir-birining ustiga chiqmasin. */}
      <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
        {/* Tenant nomi doim ko'rinadi: bir necha tenantli tizimda operator
            qaysi tashkilotda ishlayotganini bilishi kerak — noto'g'ri
            tenantda abonent yaratish tuzatish qiyin xato.
            Bir nechta a'zolik bo'lsa o'sha nomning O'ZI almashtirish
            tugmasiga aylanadi (`TenantSwitcher`) — yangi element
            qo'shilmaydi, chunki matn allaqachon to'g'ri joyda turgan edi */}
        {user ? <TenantSwitcher /> : null}
        {user ? <BranchSwitcher /> : null}

        <LanguageSwitcher />
        <ThemeToggle />

        <Dropdown
          menu={{
            items: [
              {
                key: 'signOut',
                icon: <LogoutOutlined />,
                label: t('auth.logout'),
                danger: true,
                onClick: handleSignOut,
              },
            ],
          }}
          trigger={['click']}
        >
          {/*
            ⚠️ TOR EKRANDA FAQAT AVATAR — ism matni OLIB TASHLANADI.

            O'lchangan (390px, `tmp-a6probe`): shu tugma 151px egallardi,
            o'ng guruh esa jami 285px — 390px lik ekranning uchdan ikkisi.
            Sarlavhaga 33px qolardi (A7). Avatarli tugma ~40px, ya'ni
            sarlavhaga ~150px ochiladi.

            Ism YO'QOLMAYDI: tugma bosilganda ochiladigan menyu o'sha
            joyda va u yerda joy cheklanmagan. Desktopda esa matn
            qoladi — u yerda hech narsa siqilmayapti.
          */}
          <Button type="text" style={{ height: 40 }} aria-label={user?.displayName ?? undefined}>
            <Flex align="center" gap={8}>
              <Avatar size="small" icon={<UserOutlined />} />
              {compact ? null : <span>{user?.displayName ?? ''}</span>}
            </Flex>
          </Button>
        </Dropdown>
      </Flex>
    </Layout.Header>
  )
}
